<template>
  <v-row class="px-4 license-management">
    <v-col cols="12" class="pt-0 filter-section">
      <FilterGroup @select="selectFilters" :include-advanced-filters="false" />
    </v-col>
    <v-col cols="12" md="12" class="py-0">
      <base-card>
         <v-card-text class="pa-0">
          <v-data-table
            :headers="headers"
            :items="data"
            :expanded.sync="expanded"
            :single-expand="true"
            :items-per-page="rowsPerPage"
            show-expand
            hide-default-footer
            must-sort
            class="eg-table-bnone eg-table-td-py-md mt-2 mainText--text"
            @click:row="onRowClicked"
            @item-expanded="expandRow"
            @update:sort-by="sortBy"
            @update:sort-desc="changeSortDesc"
          >
            <template v-slot:top>
              <div
                class="text-right"
                v-if="totalRecords"
              >
                {{ resultText }} of {{ totalRecords }}
              </div>
            </template>
            <template v-slot:item.name="{item}">
              <div class="d-flex align-center">
                <span class="font-weight-bold">{{ item.name || '-' }}</span>
              </div>
            </template>
            <template v-slot:item.date="{item}">
              {{ util.formatDate(item.date) }}
            </template>
            <template v-slot:item.devices_count="{item}">
              {{ util.formatSymbol(item.devices_count, 'device') }}
            </template>
            <template v-slot:item.next_expiration_date="{item}">
              {{ util.formatDate(item.next_expiration_date) }}
            </template>
            <template v-slot:item.action="{item}">
              <v-tooltip :open-delay="500" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.licensing_model === 'Co-Term' || !item.subData || item.subData.length === 0"
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    small
                    depressed
                    v-on:click.stop="() => handleOrganizationRenewClick(item)"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    Renew
                  </v-btn>
                </template>
                <span>Renew licenses for all devices in this organization.
                  {{ item.licensing_model === 'Per-Device' ? 'Expand the row to select devices to renew.' : '' }}</span>
              </v-tooltip>
              <v-menu
                v-if="item.licensing_model === 'Per-Device' && item.subData && item.subData.length > 0"
                rounded="lg"
                offset-y
              >
                <template v-slot:activator="{ attrs, on }">
                  <v-btn
                    color="primary"
                    depressed
                    small
                    class="white--text"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    Renew
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item link v-on:click.stop="() => handleOrganizationRenewClick(item, true)">
                    <v-list-item-title v-text="'All devices'"></v-list-item-title>
                  </v-list-item>
                  <v-list-item link v-on:click.stop="() => handleSelectedDevicesRenewClick(item)">
                    <v-list-item-title v-text="'Selected devices'"></v-list-item-title>
                  </v-list-item>
                  <v-list-item link v-on:click.stop="() => handleExpiredDevicesRenewClick(item)">
                    <v-list-item-title v-text="'Expired devices'"></v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td
                class="expand-table-container"
                :colspan="headers.length"
                v-if="item.id"
              >
                <LicenseManagementSubTable
                  :item="item"
                  :selectable="item.licensing_model === 'Per-Device'"
                  @load-data="loadDetailData(item, $event)"
                />
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </base-card>
    </v-col>

    <v-col cols="12">
      <pagination
        v-model="currentPage"
        :total-records="totalRecords"
        @load-data="loadData"
        @set-rows-per-page="setRowsPerPage"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

import util from '@/util'
import { sendGet } from '@/util/api'

import FilterGroup from '@/components/FilterGroup'
import Pagination from '@/components/Pagination'
import LicenseManagementSubTable from "@/components/LicenseManagementSubTable";

export default {
  components: {
    FilterGroup,
    LicenseManagementSubTable,
    Pagination,
  },

  data() {
    return {
      headers: [
        { text: 'Organization', value: 'name', cellClass: 'py-6', class: 'mainText--text' },
        { text: 'Devices', value: 'devices_count', class: 'mainText--text' },
        { text: 'License Model', value: 'licensing_model', cellClass: 'wth-250', class: 'mainText--text' },
        { text: 'Next Expiration Date', value: 'next_expiration_date', class: 'mainText--text' },
        { text: 'Action', value: 'action', class: 'mainText--text', sortable: false },
        { text: '', value: 'data-table-expand', class: 'mainText--text' },
      ],
      data: [],
      expanded: [],
      filters: {},
      startDateMenu: false,
      endDateMenu: false,
      startDate: null,
      endDate: null,
      util: util,
      currentPage: 1,
      totalRecords: 0,
      rowsPerPage: 200,
      sortKey: '',
      isSortDesc: false,
      isDifferenceColumnSort: false,
      exportLoading: false,
    }
  },

  watch: {
    startDate(val) {
      this.loadData()
    },

    endDate(val) {
      this.loadData()
    },
  },

  computed: {
    maxStartDate() {
      if (this.endDate) {
        return moment(this.endDate).format('YYYY-MM-DD')
      }

      return moment().format('YYYY-MM-DD')
    },

    minEndDate() {
      if (this.startDate) {
        return moment(this.startDate).format('YYYY-MM-DD')
      }

      return null
    },

    maxEndDate() {
      return moment().format('YYYY-MM-DD')
    },

    resultText() {
      const start = (this.currentPage - 1) * this.rowsPerPage + 1
      const end = this.currentPage * this.rowsPerPage

      return `${start} - ${end < this.totalRecords ? end : this.totalRecords}`
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    ...mapActions([
      'setLoading',
      'setLoadingSpinnerText',
      'setSnackbarMessage',
      'trackEvent',
    ]),

    selectFilters(params) {
      this.filters = Object.assign({}, params)
      this.expanded = []
      this.loadData()
    },

    setRowsPerPage(rowsPerPage) {
      this.rowsPerPage = rowsPerPage

      this.loadData(this.currentPage)
    },

    async loadData(currentPage = 1) {
      this.setLoadingSpinnerText('Loading Data...')
      this.setLoading(true)

      this.filters.showOrgsWithNoDevices = this.filters.showOrgsWithNoDevices ?? true;
      const query = util.getQueryForFilter(this.filters)

      if (this.startDate) {
        query.claim_date_after = this.startDate
      }

      if (this.endDate) {
        query.claim_date_before = this.endDate
      }

      if (this.sortKey) {
        query.ordering = this.isSortDesc ? `-${this.sortKey}` : this.sortKey
      }

      this.currentPage = currentPage
      try {
        const response = await sendGet('/cart/organization', {
          ...query,
          limit: this.rowsPerPage,
          offset: (currentPage - 1) * this.rowsPerPage,
        });
        this.totalRecords = response.count;
        this.data = response.results.map((item, index) => ({
            ...item,
            index: index + 1,
            subData: [],
            currentPage: 1,
            totalRecords: 0,
            rowsPerPage: 200,
        }));
      } catch (error) {
        this.data = [];
        this.totalRecords = 0;
        this.currentPage = 1;
      }
      this.setLoading(false);
      this.isDifferenceColumnSort = false;
    },

    onRowClicked(item, slot) {
      if (!item.id) {
        return
      }

      slot.expand(!slot.isExpanded)
    },

    expandRow({ item, value }) {
      if (!value) {
        return;
      }

      if (!item.id || item.subData.length > 0) {
        return;
      }

      this.loadDetailData(item);
      this.trackEvent({
        eventName: 'OrganizationRowExpanded',
        eventDetails: {
          orgId: item.id,
          orgName: item.name,
          orgDevicesCount: item.devices_count,
          orgLicensingModel: item.licensing_model,
        },
      });
    },

    sortBy(sortKeys) {
      this.sortKey = sortKeys[0]
      this.isSortDesc = false
      this.isDifferenceColumnSort = true
      this.loadData(this.currentPage)
    },

    changeSortDesc(sortKeysDesc) {
      if (this.isDifferenceColumnSort) {
        return
      }

      this.isSortDesc = sortKeysDesc[0]

      this.loadData(this.currentPage)
    },

    async loadDetailData(item, param = {
      currentPage: 1,
      rowsPerPage: 200,
      sortKey: '',
      isSortDesc: false,
    }) {
      item.rowsPerPage = param.rowsPerPage;
      item.currentPage = param.currentPage;
      this.setLoadingSpinnerText('Loading Organization Devices...');
      this.setLoading(true);
      try {
        const query = util.getQueryForFilter(this.filters);

        if (param.sortKey) {
          query.ordering = param.isSortDesc ? `-${param.sortKey}` : param.sortKey;
        }
        const response = await sendGet(`cart/devices`, {
          ...query,
          organization: item.id,
          limit: param.rowsPerPage,
          offset: (param.currentPage - 1) * param.rowsPerPage,
        });
        item.subData = response.results.map(el => ({...el, checked: true}));
        item.totalRecords = response.count;
      } catch (error) {
        item.subData = []
      }
      this.setLoading(false)
    },

    clearStartDate() {
      this.startDate = null
    },

    clearEndDate() {
      this.endDate = null
    },

    handleExpiredDevicesRenewClick(item) {
      this.$router.push({
        name: 'license-cart',
        params: {
          orgId: item.id,
          deviceIds: [],
          expiredOnly: true,
        },
      });

      this.trackEvent({
        eventName: 'RenewExpiredDevicesButtonClicked',
        eventDetails: {
          orgId: item.id,
          orgName: item.name,
          orgDevicesCount: item.devices_count,
          orgLicensingModel: item.licensing_model,
        },
      })
    },

    handleOrganizationRenewClick(item, allDevices=false) {
      this.$router.push({
        name: 'license-cart',
        params: {
          orgId: item.id,
          deviceIds: []
        },
      });

      this.trackEvent({
        eventName: allDevices ? 'RenewAllDevicesButtonClicked' : 'RenewOrganizationButtonClicked',
        eventDetails: {
          orgId: item.id,
          orgName: item.name,
          orgDevicesCount: item.devices_count,
          orgLicensingModel: item.licensing_model,
        },
      });
    },

    handleSelectedDevicesRenewClick(item) {
      this.$router.push({
        name: 'license-cart',
        params: {
          orgId: item.id,
          deviceIds: item.subData.filter(el => el.checked).map(el => el.id),
        },
      });

      this.trackEvent({
        eventName: 'RenewSelectedDevicesButtonClicked',
        eventDetails: {
          orgId: item.id,
          orgName: item.name,
          orgDevicesCount: item.devices_count,
          orgLicensingModel: item.licensing_model,
        },
      });
    },

    async downloadCSV() {
      this.exportLoading = true
      try {
        // Prevent the user from accidentally closing the page
        window.onbeforeunload = () => ''

        const query = util.getQueryForFilter(this.filters)
        if (this.startDate) {
          query.claim_date_after = this.startDate
        }

        if (this.endDate) {
          query.claim_date_before = this.endDate
        }

        if (this.sortKey) {
          query.ordering = this.isSortDesc ? `-${this.sortKey}` : this.sortKey
        }

        query.csv = true

        const response = await sendGet('device_claims', query)
        const blob = new Blob([response], {type: 'text/csv'})
        const filename = `DeviceClaimLog - ${new Date().toLocaleString()}.csv`;
        const link = document.createElement('a');

        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      } catch (error) {
        this.setSnackbarMessage('Error exporting a CSV, please try again later.')
      }
      this.exportLoading = false;
      window.onbeforeunload = null;
    }
  }
}
</script>

<style lang="scss">
  .license-management {
    .wth-250 {
      width: 250px;
      max-width: 250px;
      min-width: 250px;
    }

    .v-data-table {
      tbody {
        tr {
          cursor: pointer;
        }
      }

      &__expanded {
        &__content {
          box-shadow: none !important;

          td.expand-table-container {
            border-top: 1px solid #000;
            border-bottom: 0 !important;

            .sub-table {
              border-bottom: 1px solid #e0e0e0;

              tbody {
                tr {
                  cursor: auto;
                }
              }
            }
          }
        }
      }

      .v-chip {
        font-size: 11px;
        width: 120px;
        &__content {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .filter-section {
      position: relative;
      .date-range {
        border: 1px solid #9e9e9e;
        box-sizing: border-box;
        border-radius: 4px;
        max-width: 420px;
        margin-left: 0 !important;

        .v-input__slot:before {
          border-width: 0 !important;
        }

        .v-text-field .v-input__control {
          color: transparent !important;
        }

        .start-date {
          border-right: 1px solid #9e9e9e;
        }

        .v-text-field .v-input__prepend-inner {
          margin-top: 2px;
          margin-right: 4px;
        }
        .v-text-field .v-input__append-inner {
          margin-top: 2px;
        }
      }
      @media only screen and (min-width: 1600px) {
        .additional-wrapper {
          position: absolute;
          top: 12px;
          right: 0;
          display: flex;

          .v-btn {
            height: auto;
          }
        }
        .date-range {
          display: flex;
          width: 420px;
        }
      }
    }
  }
</style>
